import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import BookingCardIcons from '../../components/BookingCardIcons';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import dayjs from 'dayjs';
import { alpha, useTheme } from '@mui/material/styles';
import useNow from '../../../../hooks/useNow';
import { getBookingCardHoverBackgroundColor } from './bookingCardHoverStyles';
import { getBookingCardStatusStripeSx } from './bookingCardStatusStripes';
import {
  isInactiveBookingStatus,
  isWaitingForPaymentActionable,
  NO_SHOW_GRACE_MINUTES_AFTER_START,
} from '../../../../util/dateUtils';

/**
 * Generic vertical booking card component for displaying bookings in vertical layouts.
 * Designed to work with Grid layouts instead of absolute positioning.
 */
function VerticalBookingCardBase({
  booking,
  color = '#ffffff',
  onCardClick,
  showProductInfo = true,
  showVariationInfo = true,
  iconProps = {},
  borderStyle = 'solid',
  children,
}) {
  const {
    firstName,
    lastName,
    eventStart,
    participantCount,
    regiondoMeta,
    specialUpgrades,
    manualUpgrades,
  } = booking;
  const presentSince = booking?.presentSince;
  const finishedAt = booking?.finishedAt;
  const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
  const isEffectivelyUnpaid = !booking?.fullyPaid && hasPayableAmount;
  const nowTs = useNow();
  const isWaitingForPayment = isWaitingForPaymentActionable(booking, nowTs);
  const isAwaitingConfirmation = booking?.status === 'awaitingConfirmation';
  const isCheckedOut = !!finishedAt;
  const isInactiveCard = isInactiveBookingStatus(booking?.status);
  const theme = useTheme();
  const inactiveBackgroundColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.black, 0.54)
      : theme.palette.grey[100];
  const inactiveFilter =
    theme.palette.mode === 'dark'
      ? 'grayscale(1) brightness(0.68)'
      : 'grayscale(0.85) saturate(0.25)';
  const inactiveHoverFilter =
    theme.palette.mode === 'dark'
      ? 'grayscale(0.95) brightness(0.82)'
      : 'grayscale(0.75) saturate(0.4)';

  const showAttentionStripes = (() => {
    if (isInactiveBookingStatus(booking?.status)) return false;

    const now = dayjs();
    const startRaw = booking?.startTime || eventStart;
    const start = startRaw ? dayjs(startRaw) : null;
    const end = booking?.endTime ? dayjs(booking.endTime) : null;

    if (!presentSince && start?.isValid()) {
      const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
      if (now.isAfter(eligibleAt) || now.isSame(eligibleAt)) {
        return true;
      }
    }

    if (presentSince && !finishedAt && isEffectivelyUnpaid) return true;

    if (!finishedAt && end?.isValid()) {
      if (now.isAfter(end.add(15, 'minute')) || now.isSame(end.add(30, 'minute'))) {
        return true;
      }
    }

    return false;
  })();

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(booking);
    } else if (regiondoMeta?.booking_key) {
      // Default behavior: open Regiondo in new tab
      window.open(
        'https://www.regiondo.de/resellers/account/bookingsview/key/' + regiondoMeta.booking_key,
        '_blank'
      );
    }
  };

  return (
    <Card
      variant='outlined'
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        borderColor: color,
        borderWidth: '2px',
        borderStyle,
        cursor: 'pointer',
        transition: 'opacity 0.2s ease, filter 0.2s ease',
        ...(isCheckedOut &&
          !isInactiveCard && {
            opacity: 0.45,
            filter: 'saturate(0.3)',
          }),
        ...(isInactiveCard && {
          borderColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[500],
          borderStyle: 'dashed',
          backgroundColor: inactiveBackgroundColor,
          opacity: theme.palette.mode === 'dark' ? 0.38 : 0.45,
          filter: inactiveFilter,
        }),
        '&:hover': {
          borderColor: isInactiveCard
            ? theme.palette.mode === 'dark'
              ? theme.palette.grey[600]
              : theme.palette.grey[500]
            : color,
          backgroundColor: isInactiveCard
            ? inactiveBackgroundColor
            : getBookingCardHoverBackgroundColor(color, theme),
          ...(isCheckedOut &&
            !isInactiveCard && {
              opacity: 0.65,
              filter: 'saturate(0.5)',
            }),
          ...(isInactiveCard && {
            opacity: theme.palette.mode === 'dark' ? 0.52 : 0.65,
            filter: inactiveHoverFilter,
          }),
        },
      }}
      onClick={handleCardClick}
    >
      {isWaitingForPayment && (
        <Box
          sx={getBookingCardStatusStripeSx('237, 108, 2', 0.5)}
        />
      )}
      {showAttentionStripes && !isWaitingForPayment && (
        <Box
          sx={getBookingCardStatusStripeSx('33, 150, 243', 0.4)}
        />
      )}
      <CardContent sx={{ p: '6px 10px !important', position: 'relative', zIndex: 2 }}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Stack direction='row' alignItems='center' sx={{ flex: 1, minWidth: 0, mr: 1 }} gap={1}>
            {isAwaitingConfirmation && (
              <PendingIcon sx={{ color: 'warning.main', flexShrink: 0, width: 18, height: 18 }} titleAccess='Warten auf Bestätigung' />
            )}
            {presentSince && !finishedAt && (
              <CheckCircleIcon sx={{ color: 'success.main', flexShrink: 0, width: 18, height: 18 }} titleAccess='Eingecheckt' />
            )}
            {finishedAt && (
              <CheckCircleIcon sx={{ color: 'error.main', flexShrink: 0, width: 18, height: 18 }} titleAccess='Ausgecheckt' />
            )}
            {isEffectivelyUnpaid && (
              <PaidIcon sx={{ color: 'error.main', flexShrink: 0, width: 18, height: 18 }} titleAccess='Unbezahlt' />
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component='div'
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  lineHeight: 1,
                  fontSize: '0.92rem',
                  fontWeight: 800,
                }}
              >
                {firstName} {lastName}
              </Typography>
              {showProductInfo && regiondoMeta?.product_name ? (
                <Typography
                  component='div'
                  sx={{ fontSize: '0.76rem', opacity: 0.9, lineHeight: 1, mt: '-1px' }}
                >
                  {regiondoMeta.product_name}
                </Typography>
              ) : null}
              {showVariationInfo && regiondoMeta?.variation_name ? (
                <Typography
                  component='div'
                  sx={{ fontSize: '0.76rem', opacity: 0.85, lineHeight: 1, mt: '-1px' }}
                >
                  {regiondoMeta.variation_name}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <BookingCardIcons
            upgrades={specialUpgrades}
            manualUpgrades={manualUpgrades}
            participantCount={participantCount}
            color={color}
            {...iconProps}
          />
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

export default VerticalBookingCardBase;
